// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useMsal } from "@azure/msal-react";
import { Label, MessageBar, MessageBarType, Pivot, PivotItem, Separator, Spinner, SpinnerSize, Text } from "@fluentui/react";

import styles from "./AnalysisPanel.module.css";

import { getToken } from "../../authConfig";
import { handleError } from "../../toaster";
import { ChatAppResponse, ActiveCitation, getCitationObj, BlobUrlResponse, getBlobUrl, Thoughts } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { SupportingContent } from "../SupportingContent";
import { ThoughtProcess } from "./ThoughtProcess";
import { DocumentViewer } from "../DocumentViewer";

interface Props {
    className: string;
    activeTab?: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    sourceFile: string | undefined;
    pageNumber: string | undefined;
    answer: ChatAppResponse;
    useDarkMode: boolean;
};

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({
    answer,
    activeTab,
    activeCitation,
    sourceFile,
    pageNumber,
    onActiveTabChanged,
    className,
    useDarkMode
}: Props) => {
    const client = useMsal().instance;
    const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [citationError, setCitationError] = useState<string | undefined>(undefined);
    const [activeCitationObj, setActiveCitationObj] = useState<ActiveCitation>();
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.context.data_points.length;
    const isDisabledCitationTab: boolean = !activeCitation;
    const sourceFileExt: any = sourceFile?.split(".").pop();
    const separatorStyles = {
        root: {
            selectors: {
                '::before': {
                    backgroundColor: useDarkMode ? '#FFFFFF' : '#000000',
                },
            },
        },
    };

    async function fetchActiveCitationObj() {
        if (activeCitation) {
            try {
                const token = client ? await getToken(client) : undefined;
                const citationObj: ActiveCitation = await getCitationObj(token, activeCitation);
                if (citationObj.error) {
                    setCitationError(citationObj.error);
                } else {
                    setActiveCitationObj(citationObj);
                    setCitationError(undefined);
                }
            } catch (error) {
                setCitationError((error as Error).message);
            }
        }
    };

    async function fetchBlobUrl() {
        try {
            if (sourceFile) {
                const token = client ? await getToken(client) : undefined;
                const response: BlobUrlResponse = await getBlobUrl(token, sourceFile)
                if (response.error) {
                    setError(response.error);

                } else {
                    setBlobUrl(response.url);
                    setError(undefined);
                }
            }
        } catch (error) {
            const message = "An unexpected error occurred while fetching the document."
            setError(message);
            handleError(error, message);
        }
    };

    let thoughtProcessContent;
    const thoughts = answer.context.thoughts;

    function isThoughtsArray(value: any): value is Thoughts[] {
        return Array.isArray(value) && value.every(item =>
            typeof item === 'object' &&
            'title' in item &&
            'description' in item &&
            (!item.props || typeof item.props === 'object')
        );
    };

    if (isThoughtsArray(thoughts)) {
        // If thoughts is an array of Thoughts, render the ThoughtProcess component
        thoughtProcessContent = <ThoughtProcess thoughts={thoughts} useDarkMode={useDarkMode} />;
    } else if (typeof thoughts === 'string') {
        // If thoughts is a string, sanitize and set as inner HTML
        const sanitizedThoughts = DOMPurify.sanitize(thoughts);
        thoughtProcessContent = <div className={styles.thoughtProcess} dangerouslySetInnerHTML={{ __html: sanitizedThoughts }}></div>;
    };

    useEffect(() => {
        fetchActiveCitationObj();
    }, [activeCitation]);

    useEffect(() => {
        fetchBlobUrl();
    }, [sourceFile]);

    return (
        <div>
            <Pivot
                className={className}
                selectedKey={activeTab}
                onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}>
                <PivotItem
                    itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                    headerText="Thought process"
                    headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}>
                    {thoughtProcessContent}
                </PivotItem>
                <PivotItem
                    itemKey={AnalysisPanelTabs.SupportingContentTab}
                    headerText="Supporting content"
                    headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}>
                    <SupportingContent supportingContent={answer.context.data_points} useDarkMode={useDarkMode} />
                </PivotItem>
                <PivotItem
                    itemKey={AnalysisPanelTabs.CitationTab}
                    headerText="Citation"
                    headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}>
                    <Pivot>
                        <PivotItem itemKey="rawFile" headerText="Document">
                            <DocumentViewer
                                blobUrl={blobUrl}
                                documentExt={sourceFileExt}
                                pageNumber={pageNumber || 1}
                                error={error}
                            />
                        </PivotItem>
                        <PivotItem itemKey="indexedFile" headerText="Document Section">
                            {citationError ? (
                                <div className={styles.messageContainer}>
                                    <MessageBar
                                        messageBarType={MessageBarType.error}
                                        isMultiline={false}>
                                        Error: {citationError}
                                    </MessageBar>
                                </div>
                            ) : activeCitationObj === undefined ? (
                                <Spinner size={SpinnerSize.large} label="Loading..." />
                            ) : (
                                <div>
                                    <Separator styles={separatorStyles}>Metadata</Separator>
                                    <Label>File Name</Label><Text>{activeCitationObj.file_name.split('/').slice(1).join('/')}</Text>
                                    <Label>Title</Label><Text>{activeCitationObj.title}</Text>
                                    <Label>Section</Label><Text>{activeCitationObj.section}</Text>
                                    <Label>Page Number(s)</Label><Text>{activeCitationObj.pages?.join(",")}</Text>
                                    <Label>Token Count</Label><Text>{activeCitationObj.token_count}</Text>
                                    <Separator styles={separatorStyles}>Content</Separator>
                                    <Label>Content</Label><Text>{activeCitationObj.content}</Text>
                                </div>
                            )}
                        </PivotItem>
                    </Pivot>
                </PivotItem>
            </Pivot>
        </div>
    );
};